import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import axiosInstance from "../../services/axiosInstance";
import CommonDataGrid from "./CommonDataGrid";

/**
 * Expects the Excel/CSV to have columns (case-insensitive):
 *   name | email | rollNumber | leetcodeUsername  (or leetcode_username / leetcode username)
 */
const normaliseKey = (key) =>
  key.toLowerCase().replace(/[\s_-]/g, "");

const parseSheet = (workbook) => {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });

  return raw
    .map((row, idx) => {
      const norm = {};
      Object.entries(row).forEach(([k, v]) => {
        norm[normaliseKey(k)] = String(v).trim();
      });
      return {
        id: idx + 1, // DataGrid needs unique id
        name: norm["name"] || "",
        email: norm["email"] || "",
        rollNumber: norm["rollnumber"] || norm["roll"] || norm["rollno"] || "",
        leetcodeUsername:
          norm["leetcodeusername"] ||
          norm["leetcode"] ||
          norm["username"] ||
          "",
      };
    })
    .filter(
      (row) =>
        row.name && row.email && row.rollNumber && row.leetcodeUsername
    );
};

export default function BulkAddStudentsModal({
  open,
  onClose,
  batchId,
  onSuccess,
}) {
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  // ── File import ──────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError("");
    setResults(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "array" });
        const parsed = parseSheet(wb);
        if (parsed.length === 0) {
          setError("No rows found in the file.");
        } else {
          setRows(parsed);
        }
      } catch {
        setError("Failed to parse the file. Please check the format.");
      }
    };
    reader.readAsArrayBuffer(file);
    // reset so same file can be re-selected
    e.target.value = "";
  };

  const handleDeleteRow = (id) =>
    setRows((prev) => prev.filter((r) => r.id !== id));

  // ── Submit ───────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (rows.length === 0) {
      setError("No data to submit.");
      return;
    }
    const invalid = rows.filter((r) => !r.email && !r.leetcodeUsername);
    if (invalid.length > 0) {
      setError(
        `${invalid.length} row(s) are missing both email and LeetCode username.`
      );
      return;
    }

    setError("");
    setLoading(true);
    try {
      const { data } = await axiosInstance.post(
        `/coding-tracker/batches/${batchId}/students/bulk`,
        {
          students: rows.map(({ name, email, rollNumber, leetcodeUsername }) => ({
            name,
            email,
            rollNumber,
            leetcodeUsername,
          })),
        }
      );
      setResults(data.results);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // ── Reset & close ────────────────────────────────────────────
  const handleClose = () => {
    setRows([]);
    setFileName("");
    setResults(null);
    setError("");
    onClose();
  };

  // ── DataGrid columns ─────────────────────────────────────────
  const columns = [
    { field: "id", headerName: "#", width: 60 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 140 },
    { field: "email", headerName: "Email", flex: 1.5, minWidth: 180 },
    { field: "rollNumber", headerName: "Roll Number", flex: 1, minWidth: 130 },
    {
      field: "leetcodeUsername",
      headerName: "LeetCode Username",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Remove row">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteRow(params.row.id)}
          >
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Dialog
      open={open} onClose={handleClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 0 }}>Bulk Add Students</DialogTitle>

      <DialogContent dividers>
        {!results ? (
          <Stack spacing={3}>
            {/* ── Upload area ── */}
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'primary.light',
                borderRadius: 3,
                p: 5,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(79,70,229,0.04)' : '#f5f3ff',
                '&:hover': {
                  borderColor: '#4f46e5',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(79,70,229,0.1)' : '#ede9fe',
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }} onChange={handleFileChange}
              />
              <UploadFileRoundedIcon sx={{ fontSize: 48, color: '#4f46e5', mb: 1 }} />
              <Typography fontWeight={700} fontSize="1rem">
                {fileName || 'Click to upload Excel / CSV'}
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                Supported columns: <strong>name</strong>, <strong>email</strong>,{' '}
                <strong>rollNumber</strong>, <strong>leetcodeUsername</strong>
              </Typography>
            </Box>

            {/* ── Preview table ── */}
            {rows.length > 0 && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight={700}>Preview</Typography>
                  <Chip
                    label={`${rows.length} rows`} size="small"
                    sx={{ fontWeight: 700, bgcolor: '#ede9fe', color: '#4f46e5', border: 'none' }}
                  />
                </Stack>
                <Box sx={{ height: 350, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <CommonDataGrid
                    firstColumnField="id"
                    rows={rows} columns={columns}
                    density="compact"
                    sx={{
                      
                    }}
                  />
                </Box>
              </Box>
            )}

            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Alert severity="success" sx={{ borderRadius: 2 }}>Import complete. See summary below.</Alert>
            <ResultGroup label="✅ Added" items={results.added} color="success" />
            <ResultGroup label="⚠️ Already in batch" items={results.alreadyInBatch} color="warning" />
            <ResultGroup label="❌ Not found / Error" items={results.notFound} color="error" />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {!results ? (
          <>
            <Button onClick={handleClose} sx={{ textTransform: 'none', borderRadius: 2 }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || rows.length === 0}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3, minWidth: 150,
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                boxShadow: '0 4px 14px rgba(79,70,229,0.4)',
                '&:hover': { background: 'linear-gradient(135deg, #4338ca, #6d28d9)' },
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              Add {rows.length > 0 ? `${rows.length} Students` : 'Students'}
            </Button>
          </>
        ) : (
          <Button
            variant="contained" onClick={handleClose}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
            }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ResultGroup({ label, items, color }) {
  if (!items?.length) return null;
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} mb={0.5}>
        {label} ({items.length})
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {items.map((item) => (
          <Chip key={item} label={item} size="small" color={color} />
        ))}
      </Box>
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}
